<template>
  <UpdateNotification :show="needRefresh" @update="handleUpdate" />
  <RouterView />
</template>

<script setup lang="ts">
import { nextTick, onBeforeMount, onMounted } from 'vue'
import { RouterView } from 'vue-router'
import { useConfigStore } from '@/stores/config'
import { useThemeStore } from '@/stores/theme'
import { useBodyStore } from '@/stores/body'
import { themeConfigValue } from '@/layouts/default-layout/config/helper'
import { initializeComponents } from '@/core/plugins/keenthemes'
import { useAppVersion } from '@/composables/useAppVersion'
import UpdateNotification from '@/components/layout/UpdateNotification.vue'

const configStore = useConfigStore()
const themeStore = useThemeStore()
const bodyStore = useBodyStore()

// Initialize app version checking
const { needRefresh, updateApp } = useAppVersion()

onBeforeMount(() => {
  /**
   * Overrides the layout config using saved data from localStorage
   * remove this to use static config (@/layouts/default-layout/config/DefaultLayoutConfig.ts)
   */
  configStore.overrideLayoutConfig()

  /**
   *  Sets a mode from configuration
   */
  themeStore.setThemeMode(themeConfigValue.value)
})

onMounted(() => {
  nextTick(() => {
    initializeComponents()

    bodyStore.removeBodyClassName('page-loading')
  })
})

// Handle user-initiated update
const handleUpdate = () => {
  updateApp()
}
</script>

<style lang="scss">
@import 'bootstrap-icons/font/bootstrap-icons.css';
@import 'animate.css';
@import 'sweetalert2/dist/sweetalert2.css';
@import '@fortawesome/fontawesome-free/css/all.min.css';
@import '@vueform/multiselect/themes/default.css';
@import 'line-awesome/dist/line-awesome/css/line-awesome.css';
@import 'assets/keenicons/duotone/style.css';
@import 'assets/keenicons/outline/style.css';
@import 'assets/keenicons/solid/style.css';
@import 'assets/sass/element-ui.dark';
@import 'assets/sass/plugins';
@import 'assets/sass/style';

#app {
  display: contents;
}

/* Dark mode table hover fixes */
[data-bs-theme='dark'] {
  /* Fix table striped rows hover - use more prominent hover color */
  .table-striped > tbody > tr:hover,
  .table-striped > tbody > tr:hover > td,
  .table-striped > tbody > tr:hover > th {
    background-color: rgba(var(--bs-primary-rgb), 0.15) !important;
    color: var(--bs-body-color) !important;
  }

  /* Fix table hover class */
  .table-hover > tbody > tr:hover,
  .table-hover > tbody > tr:hover > td,
  .table-hover > tbody > tr:hover > th {
    background-color: rgba(var(--bs-primary-rgb), 0.15) !important;
    color: var(--bs-body-color) !important;
  }

  /* Fix striped rows in general */
  .table-striped > tbody > tr:nth-of-type(odd) > td,
  .table-striped > tbody > tr:nth-of-type(odd) > th {
    background-color: var(--bs-tertiary-bg);
    color: var(--bs-body-color);
  }

  /* Ensure all table cells maintain proper text color */
  .table td,
  .table th {
    color: var(--bs-body-color);
  }

  /* Fix for dataTable class specifically */
  .dataTable tbody tr:hover,
  .dataTable tbody tr:hover td,
  .dataTable tbody tr:hover th {
    background-color: rgba(var(--bs-primary-rgb), 0.15) !important;
    color: var(--bs-body-color) !important;
  }

  /* General hover fix for any table row */
  .table tbody tr:hover,
  .table tbody tr:hover > td,
  .table tbody tr:hover > th {
    background-color: rgba(var(--bs-primary-rgb), 0.15) !important;
    color: var(--bs-body-color) !important;
  }
}
</style>
